import { useAuthStore } from '@/features/shared/auth/store/useAuthStore';
import { authService } from '@/features/shared/auth/services/authService';
import { LoginCredentials, RegisterData } from '@/features/shared/auth/domain';

/**
 * Hook personalizado para manejar la autenticación
 * Proporciona una interfaz simplificada para acceder al estado de autenticación
 * y realizar acciones (login, register, logout)
 */
export const useAuth = () => {
  const store = useAuthStore();

  const login = async (credentials: LoginCredentials) => {
    store.setState({ loading: true, error: null });
    try {
      const response = await authService.login(credentials);

      // El backend devuelve success: true, user: { ... }
      if (response && response.user) {
        store.setState({
          isAuthenticated: true,
          user: response.user,
          role: response.user.role || (response.user.is_admin ? 'admin' : 'user'),
          loading: false
        });
        return response;
      } else {
        throw new Error('Respuesta inválida del servidor');
      }
    } catch (error: any) {
      store.setState({
        error: error.message || 'Error al iniciar sesión',
        loading: false
      });
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    store.setState({ loading: true, error: null });
    try {
      const response = await authService.register(data);
      store.setState({ loading: false });
      return response;
    } catch (error: any) {
      store.setState({
        error: error.message || 'Error al registrar usuario',
        loading: false
      });
      throw error;
    }
  };

  const logout = async () => {
    store.setState({ loading: true });
    try {
      await authService.logout();
    } catch (error) {
      console.error('Error durante logout:', error);
    } finally {
      store.resetState();

      // Redirigir suavemente o recargar dependiendo de dónde estemos
      if (window.location.pathname.startsWith('/admin') || window.location.pathname.startsWith('/cuenta')) {
        window.location.href = '/';
      }
    }
  };

  const resetPasswordForEmail = async (email: string) => {
    store.setState({ loading: true, error: null });
    try {
      await authService.requestPasswordReset(email);
      store.setState({ loading: false });
    } catch (error: any) {
      store.setState({
        error: error.message || 'Error al solicitar cambio de contraseña',
        loading: false
      });
      throw error;
    }
  };

  const updatePassword = async (newPassword: string, token?: string) => {
    try {
      await authService.resetPassword(newPassword, token);
      return true;
    } catch (error: any) {
      throw error;
    }
  };

  return {
    // Estado de autenticación
    user: store.user,
    isAuthenticated: store.isAuthenticated,
    loading: store.loading,
    error: store.error,
    role: store.role,
    isAdmin: store.isAdmin(),

    // Acciones asíncronas de API
    login,
    register,
    logout,
    resetPasswordForEmail,
    updatePassword,

    // Acciones de estado síncronas
    setState: store.setState,
    resetState: store.resetState,

    // Utilidades
    getUserId: () => store.user?.id,
    getUserEmail: () => store.user?.email,
    getUserName: () => (store.user as any)?.name,
    getUserRole: () => store.role || 'user',
    getToken: () => {
      // Intentar obtener de la cookie si es necesario para APIs externas
      return document.cookie.split('; ').find(row => row.startsWith('access_token='))?.split('=')[1];
    }
  };
};

export default useAuth;